import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, ModalService } from '@iota/iota-ui-components';
import { interval, map, Observable, Subscription, take } from 'rxjs';
import { DocumentUrlWithKey } from '../../../../store/reducer/document.reducer';
import {
    ConfirmationDialogData,
    DialogConfirmationComponent
} from '../../../../shared/dialogs/dialog-confirmation/dialog-confirmation.component';
import {
    DeleteCentralRegulatorLogo,
    DeleteCentralRegulatorLogoExtra,
    DeleteCompanyLogo,
    DeleteCompanyLogoExtra,
    UploadCentralRegulatorLogo,
    UploadCentralRegulatorLogoExtra,
    UploadCompanyLogo,
    UploadCompanyLogoExtra
} from '../../../../store/actions/document.action';
import { Store } from '@ngrx/store';
import { companyById } from '../../../../store/reducer/company.reducer';
import { CompanyMode } from 'src/app/shared/constants-and-enums/company-mode.enum';
import { centralRegulatorById } from 'src/app/store/reducer/central-regulators.reducer';

@Component({
    standalone: false,
    selector: 'app-company-logo-dialog',
    templateUrl: './company-logo-dialog.component.html'
})
export class CompanyLogoDialogComponent implements OnInit, OnDestroy {
    isLogoExtra: boolean;
    localLogo$: Observable<DocumentUrlWithKey>;
    selectedLogoFile: File;
    previewImageSrc: string | ArrayBuffer | null = null;
    businessPartnerId: string;
    countdown = 10;
    initials$: Observable<string>;
    progress = 0;
    isUploading = false;
    mode: CompanyMode;
    private countdownSub: Subscription;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            isLogoExtra: boolean;
            logo$: Observable<DocumentUrlWithKey>;
            businessPartnerId: string;
            mode: CompanyMode;
        },
        private dialogRef: MatDialogRef<CompanyLogoDialogComponent>,
        private store: Store,
        private dialog: ModalService
    ) {
        this.isLogoExtra = data.isLogoExtra;
        this.localLogo$ = data.logo$;
        this.businessPartnerId = data.businessPartnerId;
        this.mode = data.mode;
    }

    ngOnInit(): void {
        if (this.mode === CompanyMode.REGULATOR) {
            this.initials$ = this.store.select(centralRegulatorById(this.businessPartnerId)).pipe(
                map((regulator) =>
                    regulator?.name
                        .split(' ')
                        .map((split) => split[0])
                        .join('')
                )
            );
        } else {
            this.initials$ = this.store.select(companyById(this.businessPartnerId)).pipe(
                map((company) =>
                    company?.name
                        .split(' ')
                        .map((split) => split[0])
                        .join('')
                )
            );
        }
    }

    startCountdown() {
        this.countdownSub = interval(500)
            .pipe(take(this.countdown + 1))
            .subscribe(() => {
                this.countdown -= 1;
                this.progress = ((10 - this.countdown) / 5) * 100;
                if (this.countdown === 0) {
                    this.dialogRef.close();
                    this.countdownSub.unsubscribe();
                }
            });
    }

    onLogoSelect(event: any): void {
        const input = event.target as HTMLInputElement;
        if (input?.files && input.files.length > 0) {
            this.selectedLogoFile = input.files[0];

            const reader = new FileReader();
            reader.onload = () => {
                this.previewImageSrc = reader.result;
            };
            reader.readAsDataURL(this.selectedLogoFile);
        }
    }

    uploadLogo(): void {
        this.isUploading = true;

        if (this.selectedLogoFile) {
            if (this.mode === CompanyMode.CUSTOMER || this.mode === CompanyMode.PARTNER) {
                this.store.dispatch(UploadCompanyLogo({ file: this.selectedLogoFile, companyId: this.businessPartnerId }));
            }
            if (this.mode === CompanyMode.REGULATOR) {
                this.store.dispatch(
                    UploadCentralRegulatorLogo({ file: this.selectedLogoFile, centralRegulatorId: this.businessPartnerId })
                );
            }
        }

        this.startCountdown();
    }

    uploadLogoExtra() {
        this.isUploading = true;

        if (this.selectedLogoFile) {
            if (this.mode === CompanyMode.CUSTOMER || this.mode === CompanyMode.PARTNER) {
                this.store.dispatch(UploadCompanyLogoExtra({ file: this.selectedLogoFile, companyId: this.businessPartnerId }));
            }
            if (this.mode === CompanyMode.REGULATOR) {
                this.store.dispatch(
                    UploadCentralRegulatorLogoExtra({ file: this.selectedLogoFile, centralRegulatorId: this.businessPartnerId })
                );
            }
        }

        this.startCountdown();
    }

    deleteLogo(): void {
        const data: ConfirmationDialogData = {
            title: $localize`Delete logo`,
            text: $localize`Do you really want to delete the logo?`,
            confirmButtonStyle: 'error'
        };
        this.dialog
            .open(DialogConfirmationComponent, {
                data
            })
            .afterClosed()
            .subscribe(async (confirmed: boolean) => {
                if (confirmed) {
                    if (this.mode === CompanyMode.CUSTOMER || this.mode === CompanyMode.PARTNER) {
                        this.store.dispatch(DeleteCompanyLogo({ companyId: this.businessPartnerId }));
                    }
                    if (this.mode === CompanyMode.REGULATOR) {
                        this.store.dispatch(DeleteCentralRegulatorLogo({ centralRegulatorId: this.businessPartnerId }));
                    }
                }
            });
    }

    deleteLogoExtra(): void {
        const data: ConfirmationDialogData = {
            title: $localize`Delete logo`,
            text: $localize`Do you really want to delete the logo?`,
            confirmButtonStyle: 'error'
        };
        this.dialog
            .open(DialogConfirmationComponent, {
                data
            })
            .afterClosed()
            .subscribe(async (confirmed: boolean) => {
                if (confirmed) {
                    if (this.mode === CompanyMode.CUSTOMER || this.mode === CompanyMode.PARTNER) {
                        this.store.dispatch(DeleteCompanyLogoExtra({ companyId: this.businessPartnerId }));
                    }
                    if (this.mode === CompanyMode.REGULATOR) {
                        this.store.dispatch(DeleteCentralRegulatorLogoExtra({ centralRegulatorId: this.businessPartnerId }));
                    }
                }
            });
    }

    onCancelClick(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        if (this.countdownSub) {
            this.countdownSub.unsubscribe();
        }
    }
}
